<template>
  <div class="order-page pl-4">
    <div class="animated-bg">
      <div class="bg-shape shape-1"></div>
      <div class="bg-shape shape-2"></div>
      <div class="bg-shape shape-3"></div>
    </div>

    <div class="page-header">
      <div class="header-content px-2">
        <div class="d-flex">
          <h1>Buat Order</h1>
          <div class="header-badge ml-2">
            <v-icon size="14" color="white">mdi-cart-plus</v-icon>
            <span>Penjualan Counter</span>
          </div>
        </div>
        <p>Pilih customer, alamat pengiriman, dan produk untuk order baru</p>
      </div>
    </div>

    <ValidationObserver ref="form">
      <v-form @submit.prevent="openConfirmSave">
        <div class="main-card">
          <!-- Section: Customer & Alamat -->
          <div class="form-section">
            <div class="step-header">
              <div class="step-icon teal">
                <v-icon color="white" size="28">mdi-account-multiple-outline</v-icon>
              </div>
              <div>
                <h2>Customer & Alamat Pengiriman</h2>
                <p>Pilih customer dan toko — alamat yang ditampilkan adalah alamat toko</p>
              </div>
            </div>

            <div class="form-grid">
              <ValidationProvider name="Customer" rules="required" v-slot="{ errors }">
                <div class="form-field">
                  <label>
                    <span class="field-icon"><v-icon size="16">mdi-account</v-icon></span>
                    Customer
                    <span class="badge-required">Wajib</span>
                  </label>
                  <v-autocomplete
                    v-model="form.customer_id"
                    :items="customerList"
                    item-value="id"
                    item-text="name"
                    placeholder="Ketik nama customer untuk cari..."
                    solo
                    flat
                    dense
                    hide-details="auto"
                    class="elegant-input"
                    background-color="#f8f9fa"
                    :loading="process.customer"
                    :search-input.sync="customerSearch"
                    :filter="() => true"
                    @change="onCustomerChange"
                    clearable
                    no-filter
                    :error-messages="errors">
                  </v-autocomplete>
                </div>
              </ValidationProvider>

              <ValidationProvider name="Toko" rules="required" v-slot="{ errors }">
                <div class="form-field">
                  <label>
                    <span class="field-icon"><v-icon size="16">mdi-store</v-icon></span>
                    Toko / Store
                    <span class="badge-required">Wajib</span>
                  </label>
                  <v-autocomplete
                    v-model="form.store_id"
                    :items="storeList"
                    item-value="store_id"
                    :item-text="(item) => item.store_name || item.name || 'Toko'"
                    placeholder="Pilih toko"
                    solo
                    flat
                    dense
                    hide-details="auto"
                    class="elegant-input"
                    background-color="#f8f9fa"
                    :disabled="!form.customer_id"
                    @change="onStoreChange"
                    clearable
                    :error-messages="errors">
                  </v-autocomplete>
                </div>
              </ValidationProvider>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-map</v-icon></span>
                  Provinsi
                </label>
                <v-autocomplete
                  v-model="form.province"
                  :items="province"
                  item-value="name"
                  item-text="name"
                  placeholder="Pilih provinsi"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  @change="changeProvince"
                  clearable>
                </v-autocomplete>
              </div>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-city-variant-outline</v-icon></span>
                  Kota / Kabupaten
                </label>
                <v-autocomplete
                  v-model="form.city"
                  :items="city"
                  item-value="name"
                  item-text="name"
                  placeholder="Pilih kota"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  @change="changeCity"
                  :disabled="!form.province"
                  clearable>
                </v-autocomplete>
              </div>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-home-group</v-icon></span>
                  Kecamatan
                </label>
                <v-autocomplete
                  v-model="form.subdistrict"
                  :items="subdistrict"
                  item-value="name"
                  item-text="name"
                  placeholder="Pilih kecamatan"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  @change="changeSub"
                  :disabled="!form.city"
                  clearable>
                </v-autocomplete>
              </div>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-home-outline</v-icon></span>
                  Kelurahan
                </label>
                <v-autocomplete
                  v-model="form.village"
                  :items="village"
                  item-value="name"
                  item-text="name"
                  placeholder="Pilih kelurahan"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  :disabled="!form.subdistrict"
                  clearable>
                </v-autocomplete>
              </div>

              <ValidationProvider name="Alamat" rules="required" v-slot="{ errors }">
                <div class="form-field full-width">
                  <label>
                    <span class="field-icon"><v-icon size="16">mdi-map-marker</v-icon></span>
                    Alamat Lengkap (Toko)
                    <span class="badge-required">Wajib</span>
                  </label>
                  <v-textarea
                    v-model="form.address"
                    placeholder="Jl. Nama Jalan No. XX, RT/RW..."
                    solo
                    flat
                    dense
                    hide-details="auto"
                    class="elegant-input"
                    background-color="#f8f9fa"
                    rows="2"
                    :error-messages="errors">
                  </v-textarea>
                </div>
              </ValidationProvider>

              <div class="form-field full-width">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-magnify</v-icon></span>
                  Cari Lokasi di Peta
                </label>
                <div class="search-box">
                  <v-icon class="search-box-icon">mdi-map-search</v-icon>
                  <gmap-autocomplete
                    placeholder="Ketik alamat untuk mencari lokasi..."
                    class="search-box-input"
                    @place_changed="setPlace">
                  </gmap-autocomplete>
                </div>
              </div>

              <div class="form-field full-width">
                <div class="map-container">
                  <gmap-map
                    :center="center"
                    :zoom="15"
                    :options="{
                      zoomControl: true,
                      mapTypeControl: false,
                      scaleControl: false,
                      streetViewControl: false,
                      rotateControl: false,
                      fullscreenControl: true,
                      disableDefaultUi: false
                    }"
                    :clickable="true"
                    @click="updatePlace"
                    class="gmap-view">
                    <gmap-marker
                      :position="center"
                      :draggable="true"
                      @drag="updatePlace">
                    </gmap-marker>
                  </gmap-map>
                </div>
              </div>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-latitude</v-icon></span>
                  Latitude
                </label>
                <v-text-field
                  v-model="form.latitude"
                  placeholder="-2.9198476"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  type="number"
                  step="any">
                </v-text-field>
              </div>

              <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-longitude</v-icon></span>
                  Longitude
                </label>
                <v-text-field
                  v-model="form.longitude"
                  placeholder="104.645729"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  type="number"
                  step="any">
                </v-text-field>
              </div>
            </div>
          </div>

          <!-- Section: Produk -->
          <div class="form-section">
            <div class="step-header">
              <div class="step-icon coral">
                <v-icon color="white" size="28">mdi-package-variant</v-icon>
              </div>
              <div class="d-flex align-center flex-grow-1 justify-space-between">
                <div>
                  <h2>Produk & Qty</h2>
                  <p>Tambah varian produk dan jumlah pesanan</p>
                </div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        color="#00ADB5"
                        class="white--text ml-auto"
                        small
                        rounded
                        elevation="0"
                        :disabled="!form.store_id"
                        @click="openProductDialog">
                        <v-icon left size="18">mdi-plus</v-icon>
                        Tambah Produk
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ form.store_id ? 'Tambah produk ke order' : 'Pilih toko terlebih dahulu' }}</span>
                </v-tooltip>
              </div>
            </div>

            <div v-if="form.varian_arr.length === 0" class="empty-varian">
              <v-icon size="64" color="#EEEEEE">mdi-cart-outline</v-icon>
              <p v-if="form.store_id">Belum ada produk. Klik "Tambah Produk" untuk menambah item.</p>
              <p v-else class="text-medium-emphasis">Pilih customer dan toko terlebih dahulu, lalu klik "Tambah Produk".</p>
            </div>

            <div v-else>
              <div class="varian-table-wrap">
                <table class="varian-table">
                  <thead>
                    <tr>
                      <th>Produk / SKU</th>
                      <th class="text-center">Qty Dus</th>
                      <th class="text-right">Harga per Dus</th>
                      <th class="text-right">Diskon Strata</th>
                      <th class="text-right">Diskon Promo</th>
                      <th class="text-right">Sub Total</th>
                      <th class="text-center" style="width: 56px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.varian_arr" :key="index">
                      <td>
                        <div class="varian-name">{{ item.varian_name }}</div>
                        <div class="varian-sku">{{ item.varian_sku }}</div>
                      </td>
                      <td class="text-center">
                        <v-currency-field
                          v-model="item.qty_dus"
                          placeholder="0"
                          dense
                          hide-details
                          class="qty-input"
                          @input="onVarianQtyChange(index)">
                        </v-currency-field>
                      </td>
                      <td class="text-right">
                        Rp {{ pricePerDus(item) | price }}
                      </td>
                      <td class="text-right">
                        Rp {{ (item.diskon_strata || 0) | price }}
                      </td>
                      <td class="text-right">
                        Rp {{ (item.diskon_promo || 0) | price }}
                      </td>
                      <td class="text-right font-weight-medium">
                        Rp {{ rowSubtotal(item) | price }}
                      </td>
                      <td class="text-center">
                        <v-btn icon x-small color="#EA5455" @click="removeVarian(index)">
                          <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Struk pembayaran (sejajar dengan tabel) -->
              <div class="varian-struk-wrap">
                <table class="varian-struk-table">
                  <tbody>
                    <tr class="varian-struk-row">
                      <td class="varian-struk-label">Sub Total</td>
                      <td class="varian-struk-value text-right font-weight-medium">Rp {{ sumSubTotal | price }}</td>
                    </tr>
                    <tr class="varian-struk-row">
                      <td class="varian-struk-label">Total Diskon</td>
                      <td class="varian-struk-value text-right font-weight-medium">Rp {{ totalDiskon | price }}</td>
                    </tr>
                    <tr class="varian-struk-row varian-struk-total">
                      <td class="varian-struk-label">Total</td>
                      <td class="varian-struk-value text-right font-weight-bold">Rp {{ grandTotal | price }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Section: Pengiriman & Pembayaran -->
          <div class="form-section">
            <div class="step-header">
              <div class="step-icon orange">
                <v-icon color="white" size="28">mdi-truck-delivery-outline</v-icon>
              </div>
              <div>
                <h2>Pengiriman & Pembayaran</h2>
                <p>Tanggal rencana pengiriman dan status lunas</p>
              </div>
            </div>

            <div class="form-grid">
              <ValidationProvider name="Tanggal Rencana Kirim" rules="required" v-slot="{ errors }">
                <div class="form-field">
                  <label>
                    <span class="field-icon"><v-icon size="16">mdi-calendar</v-icon></span>
                    Tanggal Rencana Pengiriman
                    <span class="badge-required">Wajib</span>
                  </label>
                  <v-menu
                    v-model="menuDeliveryPlanDate"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedDeliveryPlanDate"
                        placeholder="Pilih tanggal"
                        solo
                        flat
                        dense
                        hide-details="auto"
                        class="elegant-input"
                        background-color="#f8f9fa"
                        readonly
                        :error-messages="errors"
                        v-bind="attrs"
                        v-on="on">
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.delivery_plan_date"
                      :min="new Date().toISOString().substr(0, 10)"
                      @input="menuDeliveryPlanDate = false">
                    </v-date-picker>
                  </v-menu>
                </div>
              </ValidationProvider>

              <div v-if="form.is_lunas === 1" class="form-field full-width">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-file-image</v-icon></span>
                  Bukti Lunas
                  <span class="badge-required">Wajib</span>
                </label>
                <div v-if="!form.bukti_lunas_url">
                  <uploadfolder v-model="upload.bukti_lunas_url">
                    <div slot="activator" class="upload-box">
                      <div v-if="!form.bukti_lunas_url && !process.bukti_lunas_url" class="upload-placeholder">
                        <div class="upload-icon-circle">
                          <v-icon size="28" color="#00ADB5">mdi-cloud-upload</v-icon>
                        </div>
                        <div class="upload-info">
                          <span class="upload-title">Upload dari galeri / file</span>
                          <span class="upload-subtitle">PNG, JPG • Maks 5MB</span>
                        </div>
                      </div>
                      <div v-if="process.bukti_lunas_url" class="upload-loading">
                        <v-progress-circular indeterminate color="#00ADB5" size="36"></v-progress-circular>
                        <span>Mengupload...</span>
                      </div>
                    </div>
                    <template v-if="!form.bukti_lunas_url && !process.bukti_lunas_url" slot="activator-camera">
                    <div class="upload-box upload-box-camera">
                      <div class="upload-placeholder">
                        <div class="upload-icon-circle">
                          <v-icon size="28" color="#00ADB5">mdi-camera</v-icon>
                        </div>
                        <div class="upload-info">
                          <span class="upload-title">Ambil foto</span>
                          <span class="upload-subtitle">Gunakan kamera</span>
                        </div>
                      </div>
                    </div>
                  </template>
                  </uploadfolder>
                </div>

                <div v-if="form.bukti_lunas_url && !process.bukti_lunas_url" class="upload-preview-card">
                  <div class="upload-preview-image">
                    <v-img :src="form.bukti_lunas_url" contain max-height="200" class="upload-preview-img"></v-img>
                    <v-btn
                      icon
                      small
                      class="upload-preview-remove"
                      color="white"
                      @click="clearBuktiLunas">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div class="upload-preview-footer">
                    <span class="upload-preview-label">Bukti lunas</span>
                    <v-btn
                      x-small
                      text
                      color="error"
                      class="upload-preview-remove-btn"
                      @click="clearBuktiLunas">
                      <v-icon x-small left>mdi-delete-outline</v-icon>
                      Hapus foto
                    </v-btn>
                  </div>
                </div>
                <div v-if="!form.bukti_lunas_url && !process.bukti_lunas_url" class="upload-hint mt-1">
                  Pilih <strong>Ambil foto</strong> untuk kamera atau <strong>Upload dari galeri</strong> untuk memilih file.
                </div>
              </div>

              <!-- <div class="form-field">
                <label>
                  <span class="field-icon"><v-icon size="16">mdi-tag-outline</v-icon></span>
                  Total Diskon (Rp)
                </label>
                <v-currency-field
                  v-model="form.total_diskon"
                  placeholder="0"
                  solo
                  flat
                  dense
                  hide-details="auto"
                  class="elegant-input"
                  background-color="#f8f9fa"
                  prefix="Rp ">
                </v-currency-field>
              </div> -->
            </div>
          </div>

          <!-- Submit -->
          <div class="form-actions d-flex justify-end">
            <v-alert v-if="message.success" type="success" dense class="mb-3" dismissible>
              {{ message.success }}
            </v-alert>
            <v-alert v-if="message.error" type="error" dense class="mb-3" dismissible>
              {{ message.error }}
            </v-alert>
            <v-btn
              type="submit"
              color="#00ADB5"
              class="white--text px-8"
              large
              rounded
              elevation="0"
              :loading="process.form"
              :disabled="process.form">
              <v-icon left>mdi-check-circle-outline</v-icon>
              Simpan Order
            </v-btn>
          </div>
        </div>
      </v-form>
    </ValidationObserver>

    <!-- Dialog Berhasil Simpan -->
    <v-dialog v-model="dialog.successSave" max-width="420" persistent>
      <v-card class="pa-4 text-center">
        <v-icon color="success" size="64" class="mb-3">mdi-check-circle</v-icon>
        <v-card-title class="pa-0 justify-center text-h6">Order berhasil disimpan</v-card-title>
        <v-card-subtitle class="pa-0 pt-2">Data order telah tersimpan.</v-card-subtitle>
        <v-card-actions class="pa-0 pt-4 justify-center">
          <v-btn color="#00ADB5" class="white--text" @click="dialog.successSave = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Konfirmasi Simpan Order -->
    <v-dialog v-model="dialog.confirmSave" max-width="420" persistent>
      <v-card class="pa-4">
        <div class="d-flex align-center mb-4">
          <v-icon color="#00ADB5" size="30" class="mr-3">mdi-help-circle-outline</v-icon>
          <div>
            <v-card-title class="pa-0 text-h6">Simpan Order?</v-card-title>
          </div>
        </div>
        <p class="pa-0 text-body-1 font-weight-medium">Pastikan data customer, toko, dan produk sudah benar.</p>
        <v-card-actions class="pa-0 pt-2 justify-end">
          <v-btn text @click="dialog.confirmSave = false">Batal</v-btn>
          <v-btn
            color="#00ADB5"
            class="white--text"
            :loading="process.form"
            :disabled="process.form"
            @click="dialog.confirmSave = false; save()">
            Ya, Simpan Order
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Tambah Produk -->
    <v-dialog v-model="dialog.product" max-width="720" persistent scrollable content-class="dialog-tambah-produk">
      <v-card class="dialog-product-card">
        <div class="dialog-product-header">
          <div class="dialog-product-header-icon">
            <v-icon color="white" size="26">mdi-package-variant-closed</v-icon>
          </div>
          <div class="dialog-product-header-text">
            <h3>Tambah Produk</h3>
            <p>Pilih produk lalu centang varian yang akan ditambahkan ke order</p>
          </div>
          <v-btn icon small class="dialog-product-close" @click="dialog.product = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="dialog-product-body">
          <div class="dialog-product-field">
            <label class="dialog-product-label">
              <v-icon size="18" class="mr-1">mdi-magnify</v-icon>
              Cari / Pilih Produk
            </label>
            <v-autocomplete
              v-model="selectedProductId"
              :items="productList"
              item-value="id"
              item-text="name"
              placeholder="Ketik nama produk..."
              solo
              flat
              dense
              hide-details
              class="dialog-product-autocomplete"
              background-color="#f8f9fa"
              @change="onProductSelect">
              <template v-slot:prepend-inner>
                <v-icon size="20" color="#393E46">mdi-package-variant</v-icon>
              </template>
            </v-autocomplete>
          </div>

          <div v-if="selectedProductId && varianOptions.length === 0" class="dialog-product-empty">
            <v-progress-circular indeterminate color="#00ADB5" size="32"></v-progress-circular>
            <span>Memuat varian...</span>
          </div>

          <div v-else-if="!selectedProductId" class="dialog-product-hint">
            <v-icon size="40" color="#EEEEEE">mdi-arrow-up</v-icon>
            <p>Pilih produk di atas untuk menampilkan daftar varian</p>
          </div>

          <div v-else-if="filteredVarianOptions.length > 0" class="dialog-product-varian">
            <div class="dialog-product-varian-head">
              <label class="dialog-product-label">
                <v-icon size="18" class="mr-1">mdi-format-list-checks</v-icon>
                Pilih Varian
                <span class="dialog-product-badge">{{ filteredVarianOptions.length }} varian</span>
              </label>
              <span v-if="selectedVarians.length > 0" class="dialog-product-selected">
                {{ selectedVarians.length }} dipilih
              </span>
            </div>
            <div class="dialog-product-table-wrap">
              <v-data-table
                v-model="selectedVarians"
                :headers="varianHeaders"
                :items="filteredVarianOptions"
                item-key="id"
                show-select
                hide-default-footer
                class="dialog-product-table"
                no-data-text="Belum ada varian"
                loading-text="Memuat...">
                <template v-slot:item.name="{ item }">
                  <span class="dialog-table-name">{{ item.name || item.varian_name }}</span>
                </template>
                <template v-slot:item.sku="{ item }">
                  <span class="dialog-table-sku">{{ item.sku || item.varian_sku }}</span>
                </template>
                <template v-slot:item.product_name="{ item }">
                  <span class="dialog-table-product">{{ item.product_name }}</span>
                </template>
              </v-data-table>
            </div>
          </div>

          <div v-else class="dialog-product-hint">
            <v-icon size="40" color="#EEEEEE">mdi-check-all</v-icon>
            <p>Semua varian untuk produk ini sudah ditambahkan ke order</p>
          </div>
        </v-card-text>

        <div class="dialog-product-actions">
          <v-btn text class="dialog-btn-cancel" @click="dialog.product = false">
            Batal
          </v-btn>
          <v-btn
            rounded
            elevation="0"
            class="dialog-btn-submit"
            :disabled="selectedVarians.length === 0"
            @click="confirmAddVarians">
            <v-icon left size="18">mdi-plus-circle-outline</v-icon>
            Tambah {{ selectedVarians.length > 0 ? `(${selectedVarians.length})` : '' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { get, post } from '@/service/Axios'

export default {
  name: 'PenjualanCounterOrder',
  components: {
    uploadfolder: () => import(/* webpackPrefetch: true */ '@/components/upload/upload-dan-kamera.vue'),
  },
  data() {
    return {
      form: {
        customer_id: '',
        store_id: '',
        province: '',
        city: '',
        subdistrict: '',
        village: '',
        address: '',
        latitude: '',
        longitude: '',
        varian_arr: [],
        total_diskon: 0,
        is_lunas: 1,
        bukti_lunas_url: '',
        delivery_plan_date: '',
        invoice_id_array: [],
      },
      customerList: [],
      defaultCustomer: null,        // <-- TAMBAHAN
      customerSearch: '',
      customerSearchTimeout: null,
      isSearchingCustomer: false,   // <-- TAMBAHAN
      storeList: [],
      promoList: [],
      strataList: [],
      promoListAll: [],
      strataListAll: [],
      province: [],
      city: [],
      subdistrict: [],
      village: [],
      productList: [],
      selectedProductId: null,
      varianOptions: [],
      selectedVarians: [],
      varianHeaders: [
        { text: 'Nama', value: 'name', sortable: false },
        { text: 'SKU', value: 'sku', sortable: false },
        { text: 'Produk', value: 'product_name', sortable: false },
      ],
      menuDeliveryPlanDate: false,
      dialog: {
        product: false,
        confirmSave: false,
        successSave: false,
      },
      upload: {
        bukti_lunas_url: null,
      },
      process: {
        customer: false,
        form: false,
        bukti_lunas_url: false,
      },
      message: {
        error: '',
        success: '',
      },
      center: {
        lat: -2.9198476,
        lng: 104.645729,
      },
    }
  },
  filters: {
    price(val) {
      if (val === undefined || val === null) return '0'
      return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0 }).format(Number(val))
    },
  },
  watch: {
    customerSearch(val) {
      if (this.customerSearchTimeout) clearTimeout(this.customerSearchTimeout)

      this.customerSearchTimeout = setTimeout(() => {

        // kalau user hapus search / clear
        if (!val) {
          this.fetchCustomerAll()
        } else {
          this.fetchCustomer(val)
        }

      }, 350)
    },
    'upload.bukti_lunas_url': {
      handler() {
        if (this.upload.bukti_lunas_url) {
          this.process.bukti_lunas_url = this.upload.bukti_lunas_url.process
          if (!this.process.bukti_lunas_url && this.upload.bukti_lunas_url.fileuri) {
            this.form.bukti_lunas_url = this.upload.bukti_lunas_url.fileuri
          }
        }
      },
      deep: true,
    },
    'form.store_id'(val) {
      if (!val) {
        this.form.varian_arr = []
        this.promoList = []
        this.strataList = []
        this.promoListAll = []
        this.strataListAll = []
      }
    },
    'form.varian_arr': {
      handler() {
        if (!this.selectedVarians || this.selectedVarians.length === 0) return
        const existsMap = this.existingVarianIdMap
        this.selectedVarians = this.selectedVarians.filter(v => !existsMap[String(v && v.id)])
      },
      deep: true,
    },
  },
  computed: {
    formattedDeliveryPlanDate() {
      const d = this.form.delivery_plan_date
      if (!d) return ''
      const date = new Date(d + 'T00:00:00')
      if (isNaN(date.getTime())) return d
      return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    // Semua total sebelum diskon (qty × harga per dus per baris)
    sumSubTotal() {
      return this.form.varian_arr.reduce((sum, item) => {
        const qty = Number(item.qty_dus) || 0
        return sum + qty * this.pricePerDus(item)
      }, 0)
    },
    // Jumlah seluruh diskon (strata + promo) semua baris
    totalDiskon() {
      return this.form.varian_arr.reduce((sum, item) => {
        const strata = Number(item.diskon_strata) || 0
        const promo = Number(item.diskon_promo) || 0
        return sum + strata + promo
      }, 0)
    },
    grandTotal() {
      return Math.max(0, this.sumSubTotal - this.totalDiskon)
    },
    existingVarianIdMap() {
      const map = Object.create(null)
      ;(this.form.varian_arr || []).forEach(v => {
        if (!v || v.varian_id === undefined || v.varian_id === null) return
        map[String(v.varian_id)] = true
      })
      return map
    },
    filteredVarianOptions() {
      const existsMap = this.existingVarianIdMap
      return (this.varianOptions || []).filter(v => !existsMap[String(v && v.id)])
    },
  },
  created() {
    this.fetchCustomerCounter()
    this.fetchProv()
    this.fetchProduct()
    this.setDefaultDeliveryDate()
  },
  methods: {
    clearBuktiLunas() {
      this.form.bukti_lunas_url = ''
      this.upload.bukti_lunas_url = null
      this.process.bukti_lunas_url = false
    },
    setDefaultDeliveryDate() {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      this.form.delivery_plan_date = d.toISOString().slice(0, 10)
    },
    async fetchCustomerCounter() {
      this.process.customer = true
      try {
        const baseUrl = window.location.origin
        const defaultCustomerId = baseUrl === 'https://mega.mitrawarung.id' ? '4605' : '4254'

        const { data } = await get('admin/customer/data_approved', {
          params: { id: defaultCustomerId }
        })

        if (data.status === 200) {
          const cust = (data.results.data || [])[0]

          // simpan sebagai pinned customer
          this.defaultCustomer = cust

          // tampil pertama kali
          this.customerList = cust ? [cust] : []

          // ambil list normal
          await this.fetchCustomerAll()
        }
      } catch (e) {
        this.message.error = 'Gagal memuat data customer'
      }
      this.process.customer = false
    },
    async fetchCustomerAll(search = '') {
      this.process.customer = true
      this.isSearchingCustomer = false

      const params = {
        is_blacklist: '0',
        limit: 10,
        page: 1,
      }

      try {
        const { data } = await get('admin/customer/data_approved', { params })
        if (data.status === 200) {

          let list = data.results.data || []

          // buang 4254 dari list agar tidak dobel
          if (this.defaultCustomer) {
            list = list.filter(c =>
              String(c.id) !== String(this.defaultCustomer.id)
            )
          }

          // gabungkan: default selalu di atas
          this.customerList = this.defaultCustomer
            ? [this.defaultCustomer, ...list]
            : list
        }
      } catch (e) {
        this.message.error = 'Gagal memuat data customer'
      }

      this.process.customer = false
    },
    async fetchCustomer(search = '') {
      this.process.customer = true
      this.isSearchingCustomer = true

      const params = {
        is_blacklist: '0',
        limit: 20,
        page: 1,
        search: search || undefined,
      }

      try {
        const { data } = await get('admin/customer/data_approved', { params })
        if (data.status === 200) {
          this.customerList = data.results.data || []
        }
      } catch (e) {
        this.message.error = 'Gagal memuat data customer'
      }

      this.process.customer = false
    },
    async onCustomerChange() {
      this.form.store_id = ''
      this.form.varian_arr = []
      this.promoList = []
      this.strataList = []
      this.promoListAll = []
      this.strataListAll = []
      this.form.province = ''
      this.form.city = ''
      this.form.subdistrict = ''
      this.form.village = ''
      this.form.address = ''
      this.form.latitude = ''
      this.form.longitude = ''
      this.storeList = []
      this.city = []
      this.subdistrict = []
      this.village = []
      this.center = { lat: -2.9198476, lng: 104.645729 }
      if (!this.form.customer_id) return
      try {
        const { data } = await get('admin/customer/detail', { params: { id: this.form.customer_id } })
        if (data.status === 200 && data.results.data) {
          const c = data.results.data
          this.storeList = (c.store && Array.isArray(c.store)) ? c.store : []
          // console.log(this.storeList)
          this.form.store_id = this.storeList[0].store_id
          // console.log(this.form.store_id)
          this.onStoreChange()
        }
      } catch (e) {
        this.message.error = 'Gagal memuat detail customer'
      }
    },
    async onStoreChange() {
      const sid = this.form.store_id
      if (sid === undefined || sid === null || sid === '') return
      const store = this.storeList.find(s => String(s.store_id || s.id) === String(sid))
      if (!store) return
      this.form.province = store.store_province_name || store.province_name || ''
      this.form.city = store.store_city_name || store.city_name || ''
      this.form.subdistrict = store.store_subdistrict_name || store.subdistrict_name || ''
      this.form.village = store.store_village_name || store.village_name || ''
      this.form.address = store.store_address || store.address || ''
      const lat = parseFloat(store.store_latitude || store.latitude)
      const lng = parseFloat(store.store_longitude || store.longitude)
      this.form.latitude = (isNaN(lat) ? '' : lat).toString()
      this.form.longitude = (isNaN(lng) ? '' : lng).toString()
      if (!isNaN(lat) && !isNaN(lng)) {
        this.center = { lat, lng }
      }
      const norm = (a, b) => (a && b && String(a).toUpperCase().trim() === String(b).toUpperCase().trim())
      const p = this.province.find(x => norm(x.name, this.form.province))
      if (p && p.id) {
        this.form.province = p.name
        await this.fetchCity(p.id)
        const c = this.city.find(x => norm(x.name, this.form.city))
        if (c && c.id) {
          this.form.city = c.name
          await this.fetchSub(c.id)
          const s = this.subdistrict.find(x => norm(x.name, this.form.subdistrict))
          if (s && s.id) {
            this.form.subdistrict = s.name
            await this.fetchVil(s.id)
            const v = this.village.find(x => norm(x.name, this.form.village))
            if (v && v.name) this.form.village = v.name
          }
        }
      }
      await this.fetchPromoByStore()
      await this.fetchStrataByStore()
      await this.fetchPromoData()
      await this.fetchStrataData()
    },
    async fetchPromoByStore() {
      const sid = this.form.store_id
      if (!sid) {
        this.promoList = []
        return
      }
      try {
        const { data } = await get('admin/promo/data_promo_store', {
          params: { store_id: sid, pagination_bool: false },
        })
        if (data.status === 200 && data.results && data.results.data) {
          this.promoList = data.results.data
        } else {
          this.promoList = []
        }
      } catch (_) {
        this.promoList = []
      }
    },
    async fetchStrataByStore() {
      const sid = this.form.store_id
      if (!sid) {
        this.strataList = []
        return
      }
      try {
        const { data } = await get('admin/strata_price/strata_price_store', {
          params: { store_id: sid, pagination_bool: false },
        })
        if (data.status === 200 && data.results && data.results.data) {
          this.strataList = data.results.data
        } else {
          this.strataList = []
        }
      } catch (_) {
        this.strataList = []
      }
    },
    async fetchPromoData() {
      try {
        const { data } = await get('admin/promo/data', {
          params: { pagination_bool: false, limit: 999999 },
        })
        if (data.status === 200 && data.results && data.results.data) {
          this.promoListAll = data.results.data
        } else {
          this.promoListAll = []
        }
      } catch (_) {
        this.promoListAll = []
      }
    },
    async fetchStrataData() {
      try {
        const { data } = await get('admin/strata_price/data', {
          params: { pagination_bool: false, limit: 999999 },
        })
        if (data.status === 200 && data.results && data.results.data) {
          this.strataListAll = data.results.data
        } else {
          this.strataListAll = []
        }
      } catch (_) {
        this.strataListAll = []
      }
    },
    getPromoForVarian(varianId) {
      if (!varianId) return []
      // Jika toko tidak punya promo (data_promo_store []), jangan pakai promo sama sekali
      if (!(this.promoList || []).length) return []
      let list = (this.promoListAll || []).filter(
        p => String(p.product_varian_id) === String(varianId)
      )
      const storePromoIds = this.promoList.map(p => p.promo_id)
      list = list.filter(p => storePromoIds.includes(String(p.id)))
      return list
    },
    getStrataForVarian(varianId) {
      if (!varianId) return []
      // Jika toko tidak punya strata (strata_price_store []), jangan pakai strata sama sekali
      if (!(this.strataList || []).length) return []
      let list = (this.strataListAll || []).filter(
        s => String(s.varian_id) === String(varianId)
      )
      const storeStrataIds = this.strataList.map(s => s.price_strata_id)
      list = list.filter(s => storeStrataIds.includes(String(s.id)))
      return list
    },
    applyPromoAndStrata() {
      this.form.varian_arr.forEach(item => this.applyPromoAndStrataForRow(item))
    },
    applyPromoAndStrataForRow(item) {
      const qty = Number(item.qty_dus) || 0
      const hargaPerDus = this.pricePerDus(item)
      const subtotalRow = qty * hargaPerDus

      item.strata_id = 0
      item.strata_nominal = 0
      item.diskon_strata = 0
      const strataList = this.getStrataForVarian(item.varian_id)
      const strataMatch = strataList.find(s => {
        const min = Number(s.min) || 0
        const max = Number(s.max) || 999999
        return qty >= min && qty <= max
      })
      if (strataMatch) {
        item.strata_id = strataMatch.id
        const nominal = Number(strataMatch.nominal) || 0
        item.strata_nominal = nominal
        item.diskon_strata = nominal * qty
      }

      item.promo_id = 0
      item.diskon_promo = 0
      const promoList = this.getPromoForVarian(item.varian_id)
      const promoMatch = promoList.find(p => {
        const minTrx = Number(p.minimum_transaksi) || 0
        if (minTrx <= 0) return true
        const type = (p.minimum_transaksi_type || '').toLowerCase()
        const unit = (p.minimum_transaksi_unit || '').toLowerCase()
        if (type === 'qty') {
          if (unit === 'dus') return qty >= minTrx
          return qty >= minTrx
        }
        if (type === 'value') return subtotalRow >= minTrx
        return qty >= minTrx
      })
      if (promoMatch) {
        item.promo_id = promoMatch.id
        const percent = Number(promoMatch.percent) || 0
        const nominal = Number(promoMatch.nominal) || 0
        if (percent > 0) {
          item.diskon_promo = Math.round((subtotalRow * percent) / 100)
        } else {
          item.diskon_promo = nominal * qty
        }
      }
    },
    setPlace(place) {
      if (!place || !place.geometry || !place.geometry.location) return
      this.center = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }
      this.form.latitude = place.geometry.location.lat()
      this.form.longitude = place.geometry.location.lng()
      if (place.formatted_address) {
        this.form.address = place.formatted_address
      }
    },
    updatePlace(location) {
      if (!location || !location.latLng) return
      this.center = {
        lat: location.latLng.lat(),
        lng: location.latLng.lng(),
      }
      this.form.latitude = location.latLng.lat()
      this.form.longitude = location.latLng.lng()
    },
    async fetchProv() {
      try {
        const { data } = await get('ref/provinsi', { params: { limit: 999999 } })
        if (data.status === 200) this.province = data.results.data || []
      } catch (_) {}
    },
    changeProvince() {
      this.form.city = ''
      this.form.subdistrict = ''
      this.form.village = ''
      this.city = []
      this.subdistrict = []
      this.village = []
      const p = this.province.find(x => x.name === this.form.province)
      if (p && p.id) this.fetchCity(p.id)
    },
    async fetchCity(provinceId) {
      try {
        const { data } = await get('ref/kota', { params: { limit: 999999, province_id: provinceId } })
        if (data.status === 200) this.city = data.results.data || []
      } catch (_) {}
    },
    changeCity() {
      this.form.subdistrict = ''
      this.form.village = ''
      this.subdistrict = []
      this.village = []
      const c = this.city.find(x => x.name === this.form.city)
      if (c && c.id) this.fetchSub(c.id)
    },
    async fetchSub(cityId) {
      try {
        const { data } = await get('ref/kecamatan', { params: { limit: 999999, city_id: cityId } })
        if (data.status === 200) this.subdistrict = data.results.data || []
      } catch (_) {}
    },
    changeSub() {
      this.form.village = ''
      this.village = []
      const s = this.subdistrict.find(x => x.name === this.form.subdistrict)
      if (s && s.id) this.fetchVil(s.id)
    },
    async fetchVil(subdistrictId) {
      try {
        const { data } = await get('ref/kelurahan', { params: { limit: 999999, subdistrict_id: subdistrictId } })
        if (data.status === 200) this.village = data.results.data || []
      } catch (_) {}
    },
    async fetchProduct() {
      try {
        const { data } = await get('admin/product/data', { params: { limit: 999999 } })
        if (data.status === 200) this.productList = data.results.data || []
      } catch (_) {}
    },
    openProductDialog() {
      this.selectedProductId = null
      this.varianOptions = []
      this.selectedVarians = []
      this.dialog.product = true
    },
    onProductSelect() {
      this.varianOptions = []
      this.selectedVarians = []
      if (!this.selectedProductId) return
      this.fetchVarianFromAdmin()
    },
    async fetchVarianFromAdmin() {
      if (!this.selectedProductId) return
      try {
        const { data } = await get('admin/varian/data', {
          params: { limit: 999999, product_id: this.selectedProductId },
        })
        if (data.status === 200 && data.results.data) {
          const product = this.productList.find(p => p.id === this.selectedProductId)
          this.varianOptions = (data.results.data || []).map(v => ({
            ...v,
            name: v.name || v.varian_name,
            sku: v.sku || v.varian_sku,
            product_name: (product && product.name) || '',
            total_in_dus: Number(v.total_in_dus) || 0,
            pricelist_pcs_ppn: Number(v.pricelist_pcs_ppn) || 0,
          }))
        }
      } catch (_) {}
    },
    confirmAddVarians() {
      const toAdd = this.selectedVarians.map(v => ({
        varian_id: v.id,
        varian_sku: v.sku || v.varian_sku,
        varian_name: v.name || v.varian_name,
        varian_images: (v.images && typeof v.images === 'string') ? v.images : (v.images ? JSON.stringify(v.images) : '[]'),
        qty_pcs: 0,
        qty_dus: 0,
        qty_lusin: 0,
        price_pcs: 0,
        price_dus: 0,
        price_lusin: 0,
        total_in_dus: Number(v.total_in_dus) || 0,
        pricelist_pcs_ppn: Number(v.pricelist_pcs_ppn) || 0,
        diskon: 0,
        promo_id: 0,
        strata_nominal: 0,
        strata_id: 0,
        diskon_strata: 0,
        diskon_promo: 0,
      }))
      toAdd.forEach(item => {
        const exists = this.form.varian_arr.find(x => x.varian_id === item.varian_id)
        if (exists) {
          exists.qty_dus = (Number(exists.qty_dus) || 0) + (Number(item.qty_dus) || 0)
        } else {
          this.form.varian_arr.push(item)
        }
      })
      this.applyPromoAndStrata()
      this.dialog.product = false
    },
    removeVarian(index) {
      this.form.varian_arr.splice(index, 1)
    },
    onVarianQtyChange() {
      this.applyPromoAndStrata()
    },
    pricePerDus(item) {
      const p = Number(item.pricelist_pcs_ppn) || 0
      const t = Number(item.total_in_dus) || 1
      return Math.round(p * t)
    },
    rowSubtotal(item) {
      const qty = Number(item.qty_dus) || 0
      const harga = this.pricePerDus(item)
      const strata = Number(item.diskon_strata) || 0
      const promo = Number(item.diskon_promo) || 0
      return Math.max(0, qty * harga - strata - promo)
    },
    scrollToFirstError() {
      this.$nextTick(() => {
        setTimeout(() => {
          const form = this.$refs.form
          if (!form || !form.errors) return
          const errors = Object.entries(form.errors)
            .map(([key, value]) => ({ key, value }))
            .filter(error => error.value && error.value.length)
          if (errors.length > 0 && form.refs && form.refs[errors[0].key]) {
            const el = form.refs[errors[0].key].$el
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }
        }, 10)
      })
    },
    async openConfirmSave() {
      this.message.error = ''
      const valid = await this.$refs.form.validate()
      if (!valid) {
        this.scrollToFirstError()
        return
      }

      const hasQty = this.form.varian_arr.some(v => (Number(v.qty_dus) || 0) > 0)
      if (!hasQty) {
        this.message.error = 'Minimal satu produk harus memiliki qty lebih dari 0.'
        return
      }
      if (this.form.is_lunas === 1 && !this.form.bukti_lunas_url) {
        this.message.error = 'Bukti lunas wajib diupload jika memilih langsung lunas.'
        return
      }
      this.dialog.confirmSave = true
    },
    async save() {
      this.message.error = ''
      const valid = await this.$refs.form.validate()
      if (!valid) {
        this.scrollToFirstError()
        return
      }

      const hasQty = this.form.varian_arr.some(v => (Number(v.qty_dus) || 0) > 0)
      if (!hasQty) {
        this.message.error = 'Minimal satu produk harus memiliki qty lebih dari 0.'
        return
      }
      if (this.form.is_lunas === 1 && !this.form.bukti_lunas_url) {
        this.message.error = 'Bukti lunas wajib diupload jika memilih langsung lunas.'
        return
      }

      const payload = {
        customer_id: this.form.customer_id,
        province: this.form.province,
        city: this.form.city,
        subdistrict: this.form.subdistrict,
        village: this.form.village,
        address: this.form.address,
        latitude: this.form.latitude,
        longitude: this.form.longitude,
        store_id: this.form.store_id,
        total_diskon: Number(this.totalDiskon) || 0,
        is_lunas: Number(this.form.is_lunas) || 0,
        delivery_plan_date: this.form.delivery_plan_date,
        invoice_id_array: Array.isArray(this.form.invoice_id_array) ? this.form.invoice_id_array : [],
        varian_arr: this.form.varian_arr.map(v => {
          const qtyDus = Number(v.qty_dus) || 0
          const priceDus = this.pricePerDus(v)
          const pricePcs = Number(v.pricelist_pcs_ppn) || 0
          const totalInDus = Number(v.total_in_dus) || 1
          return {
            varian_id: v.varian_id,
            varian_sku: v.varian_sku || '',
            varian_name: v.varian_name || '',
            varian_images: v.varian_images || '[]',
            qty_pcs: 0,
            qty_dus: qtyDus,
            qty_lusin: 0,
            price_pcs: pricePcs,
            price_dus: priceDus,
            price_lusin: 0,
            total_in_dus: totalInDus,
            diskon: (Number(v.diskon_strata) || 0) + (Number(v.diskon_promo) || 0),
            promo_id: Number(v.promo_id) || 0,
            strata_nominal: Number(v.strata_nominal) || 0,
            strata_id: Number(v.strata_id) || 0,
          }
        }),
      }
      if (this.form.is_lunas === 1 && this.form.bukti_lunas_url) {
        payload.bukti_lunas_url = this.form.bukti_lunas_url
      }

      payload.varian_arr = JSON.stringify(payload.varian_arr)

      this.process.form = true
      await post('admin/order/add_admin', { data: payload })
        .then((response) => {
          const res = response.data
          this.process.form = false
          if (res.status === 200) {
            this.message.error = ''
            this.form.varian_arr = []
            this.form.customer_id = ''
            this.form.store_id = ''
            this.form.bukti_lunas_url = ''
            this.upload.bukti_lunas_url = null
            this.storeList = []
            this.dialog.successSave = true
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            this.message.error = res.message || 'Gagal menyimpan order'
          }
        })
        .catch((e) => {
          this.process.form = false
          this.message.error = (e.response && e.response.data && e.response.data.message) || 'Gagal menyimpan order'
        })
    },
  },
}
</script>

<style scoped>
.order-page {
  position: relative;
  padding: 18px;
  padding-top: 36px;
  min-height: 100%;
  background: linear-gradient(180deg, #fcfcfc 0%, #f8fafa 100%);
}

.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.6;
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #00ADB5 0%, #00ADB5 100%);
  top: -100px;
  right: -50px;
  filter: blur(60px);
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #FFD460 0%, #F07B3F 100%);
  bottom: 100px;
  left: -50px;
  filter: blur(50px);
  animation-delay: -5s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #EA5455 0%, #EA5455 100%);
  top: 50%;
  right: 20%;
  filter: blur(40px);
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-20px) rotate(5deg); }
  50% { transform: translateY(0) rotate(0deg); }
  75% { transform: translateY(20px) rotate(-5deg); }
}

.page-header {
  position: relative;
  z-index: 1;
  margin-bottom: 28px;
}

.header-content h1 {
  font-size: 18px;
  font-weight: 700;
  color: #222831;
  margin: 0 0 4px;
}

.header-content p {
  font-size: 14px;
  color: #393E46;
  margin: 0;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: linear-gradient(135deg, #00ADB5 0%, #00ADB5 100%);
  border-radius: 20px;
}

.header-badge span {
  font-size: 11px;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.main-card {
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 30px rgba(34, 40, 49, 0.08);
  border: 1px solid rgba(238, 238, 238, 0.6);
  overflow: hidden;
}

.form-section {
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f8f9fa;
}

.step-icon {
  width: 42px;
  height: 42px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon.teal {
  background: linear-gradient(135deg, #00ADB5 0%, #00ADB5 100%);
  box-shadow: 0 8px 20px rgba(0, 173, 181, 0.3);
}

.step-icon.coral {
  background: linear-gradient(135deg, #EA5455 0%, #EA5455 100%);
  box-shadow: 0 8px 20px rgba(234, 84, 85, 0.3);
}

.step-icon.orange {
  background: linear-gradient(135deg, #F07B3F 0%, #F07B3F 100%);
  box-shadow: 0 8px 20px rgba(240, 123, 63, 0.3);
}

.step-header h2 {
  font-size: 14px;
  font-weight: 700;
  color: #222831;
  margin: 0 0 4px;
}

.step-header p {
  font-size: 12px;
  color: #393E46;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-field.full-width {
  grid-column: span 2;
}

.form-field label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #222831;
  margin-bottom: 10px;
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #f8f9fa;
  border-radius: 6px;
}

.field-icon .v-icon {
  color: #393E46 !important;
}

.badge-required {
  margin-left: auto;
  font-size: 10px;
  font-weight: 600;
  color: #EA5455;
  background: rgba(234, 84, 85, 0.1);
  padding: 3px 8px;
  border-radius: 4px;
}

.elegant-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 44px !important;
  border: 1px solid #e8eaed !important;
  background: #fafbfc !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.elegant-input >>> .v-input--is-focused .v-input__slot {
  background: #ffffff !important;
  border-color: #00ADB5 !important;
  box-shadow: 0 0 0 3px rgba(0, 173, 181, 0.12) !important;
}

/* Search box & map (alamat toko) */
.search-box {
  position: relative;
}

.search-box-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #393E46 !important;
  z-index: 1;
}

.search-box-input {
  width: 100%;
  height: 44px;
  padding: 0 16px 0 44px;
  background: #fafbfc;
  border: 1.5px solid #e8eaed;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #222831;
  outline: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.search-box-input:hover {
  border-color: #d0d5dd;
  background: #f5f6f7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.search-box-input:focus {
  background: #ffffff;
  border-color: #00ADB5;
  box-shadow: 0 0 0 3px rgba(0, 173, 181, 0.12), 0 1px 2px rgba(0, 0, 0, 0.05);
}

.search-box-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.map-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(34, 40, 49, 0.1);
  border: 2px solid #EEEEEE;
}

.gmap-view {
  width: 100%;
  height: 280px;
}

.empty-varian {
  text-align: center;
  padding: 48px 24px;
  background: #fafbfc;
  border-radius: 10px;
  border: 2px dashed #e8eaed;
}

.empty-varian p {
  margin: 12px 0 0;
  font-size: 14px;
  color: #393E46;
}

.varian-table-wrap {
  overflow-x: auto;
  border: 1px solid #EEEEEE;
  border-radius: 12px 12px 0 0;
  border-bottom: none;
  background: #fff;
}

.varian-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.varian-table th,
.varian-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #EEEEEE;
  text-align: left;
}

.varian-table th {
  background: #f8f9fa;
  color: #222831;
  font-weight: 600;
}

/* Struk pembayaran (satu blok dengan tabel) */
.varian-struk-wrap {
  margin-top: 0;
  border: 1px solid #EEEEEE;
  border-top: none;
  border-radius: 0 0 12px 12px;
  background: #f8f9fa;
  overflow: hidden;
}

.varian-struk-table {
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
}

.varian-struk-table td {
  padding: 12px 16px;
  vertical-align: middle;
  border: none;
}

.varian-struk-row .varian-struk-label {
  color: #393E46;
  width: 1%;
  white-space: nowrap;
}

.varian-struk-row .varian-struk-value {
  color: #222831;
}

.varian-struk-total .varian-struk-label {
  padding-top: 14px;
  border-top: 2px solid #EEEEEE;
  font-weight: 600;
  color: #222831;
}

.varian-struk-total .varian-struk-value {
  padding-top: 14px;
  border-top: 2px solid #EEEEEE;
  color: #00ADB5;
  font-size: 18px;
}

.varian-name {
  font-weight: 600;
  color: #222831;
}

.varian-sku {
  font-size: 12px;
  color: #393E46;
}

.qty-input {
  max-width: 90px;
}

.qty-input >>> .v-input__slot {
  border-radius: 6px !important;
  padding: 0 10px !important;
  border: 1px solid #e8eaed !important;
}

.form-actions {
  padding: 24px;
  border-top: 1px solid #f0f0f0;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #222831;
  margin-bottom: 8px;
}

.border-radius-card {
  border-radius: 12px;
}

/* Dialog Tambah Produk */
.dialog-tambah-produk .v-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(34, 40, 49, 0.15);
}

.dialog-product-card {
  border-radius: 16px;
  overflow: hidden;
}

.dialog-product-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #00ADB5 0%, #009ba3 100%);
  position: relative;
}

.dialog-product-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-product-header-text h3 {
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
}

.dialog-product-header-text p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.dialog-product-close {
  position: absolute;
  top: 12px;
  right: 12px;
  color: rgba(255, 255, 255, 0.9) !important;
}

.dialog-product-close:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
}

.dialog-product-body {
  padding: 24px !important;
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-product-field {
  margin-bottom: 20px;
}

.dialog-product-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #222831;
  margin-bottom: 10px;
}

.dialog-product-autocomplete >>> .v-input__slot {
  border-radius: 10px !important;
  border: 1px solid #e8eaed !important;
  min-height: 48px !important;
}

.dialog-product-autocomplete >>> .v-input__slot:hover,
.dialog-product-autocomplete >>> .v-input--is-focused .v-input__slot {
  border-color: #00ADB5 !important;
  box-shadow: 0 0 0 2px rgba(0, 173, 181, 0.12) !important;
}

.dialog-product-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  background: #f8f9fa;
  border-radius: 12px;
  color: #393E46;
  font-size: 14px;
}

.dialog-product-hint {
  text-align: center;
  padding: 40px 24px;
  background: #fafbfc;
  border-radius: 12px;
  border: 2px dashed #e8eaed;
}

.dialog-product-hint p {
  margin: 12px 0 0;
  font-size: 14px;
  color: #393E46;
}

.dialog-product-varian {
  margin-top: 4px;
}

.dialog-product-varian-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.dialog-product-badge {
  font-size: 11px;
  font-weight: 600;
  color: #00ADB5;
  background: rgba(0, 173, 181, 0.1);
  padding: 4px 10px;
  border-radius: 20px;
  margin-left: 8px;
}

.dialog-product-selected {
  font-size: 12px;
  font-weight: 600;
  color: #00ADB5;
}

.dialog-product-table-wrap {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #EEEEEE;
  background: #fff;
}

.dialog-product-table >>> .v-data-table__wrapper {
  max-height: 280px;
}

.dialog-product-table >>> table {
  font-size: 13px;
}

.dialog-product-table >>> thead th {
  background: #f8f9fa !important;
  color: #222831 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 16px !important;
}

.dialog-product-table >>> tbody td {
  padding: 12px 16px !important;
}

.dialog-product-table >>> tbody tr:hover {
  background: #f8fafa !important;
}

.dialog-product-table >>> .v-data-table__expand-icon {
  color: #00ADB5 !important;
}

.dialog-table-name {
  font-weight: 600;
  color: #222831;
}

.dialog-table-sku {
  font-size: 12px;
  color: #393E46;
}

.dialog-table-product {
  font-size: 12px;
  color: #393E46;
}

.dialog-product-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #fafbfc;
  border-top: 1px solid #EEEEEE;
}

.dialog-btn-cancel {
  color: #393E46 !important;
  font-weight: 600;
  text-transform: none;
}

.dialog-btn-submit {
  background: linear-gradient(135deg, #00ADB5 0%, #009ba3 100%) !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  padding: 0 24px !important;
  min-width: 140px;
}

.dialog-btn-submit:not([disabled]):hover {
  box-shadow: 0 4px 16px rgba(0, 173, 181, 0.35) !important;
}

.dialog-btn-submit[disabled] {
  opacity: 0.5;
}

/* Upload box (bukti lunas) */
.upload-box {
  border: 1.5px dashed #d0d5dd;
  border-radius: 10px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fafbfc;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.upload-box:hover {
  border-color: #00ADB5;
  border-style: solid;
  background: rgba(0, 173, 181, 0.04);
  box-shadow: 0 0 0 3px rgba(0, 173, 181, 0.08);
}
.upload-placeholder,
.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.upload-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: white;
  border: 2px solid #EEEEEE;
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-info { text-align: center; }
.upload-title { display: block; font-size: 14px; font-weight: 600; color: #222831; margin-bottom: 4px; }
.upload-subtitle { font-size: 12px; color: #393E46; }
.upload-loading span { font-size: 13px; color: #393E46; }
.upload-preview-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fafbfc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.upload-preview-image {
  position: relative;
  width: 100%;
  min-height: 120px;
  max-height: 200px;
  background: #f1f3f4;
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-preview-img {
  border-radius: 0;
}
.upload-preview-remove {
  position: absolute !important;
  top: 8px;
  right: 8px;
  min-width: 32px !important;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.5) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
}
.upload-preview-remove:hover {
  background: rgba(220, 38, 38, 0.9) !important;
  opacity: 1;
}
.upload-preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}
.upload-preview-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}
.upload-preview-remove-btn {
  text-transform: none;
  font-size: 12px;
  font-weight: 500;
  min-width: auto;
  padding: 0 6px;
}
.upload-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0, 173, 181, 0.9), rgba(0, 173, 181, 0.9));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 12px;
}
.upload-overlay span { font-size: 12px; color: white; font-weight: 600; }
.upload-box:hover .upload-overlay { opacity: 1; }
.upload-hint { font-size: 12px; color: #6b7280; }
.upload-box-camera { flex: 1; min-width: 160px; }
</style>
